// Importa Navigate para redirecciones
import { Navigate } from "react-router-dom";
// Importa jwtDecode para decodificar el token JWT
import { jwtDecode } from "jwt-decode";
// Hook personalizado para manejar token de autenticación
import { useAuthToken } from "./hooks/useAuthToken";
import { JSX } from "react";

// Define la forma del token decodificado (solo lo que necesitamos)
interface DecodedToken {
  exp: number;      // Timestamp de expiración
  roles?: string[]     // Roles del usuario (usuario, profesional, admin)
}

// Props que recibe el componente
interface PrivateRouteProps {
  children: JSX.Element; // Componente hijo a renderizar si pasa validaciones
  roles?: string[];      // Lista de roles permitidos para esta ruta
}

// Componente que protege rutas privadas
const PrivateRoute = ({ children, roles }: PrivateRouteProps) => {
  // Obtener token y posible error usando el hook
  const { token, error } = useAuthToken();

  // Si no hay token o hay error, redirigir al login
  if (!token || error) {
    return <Navigate to="/" replace />;
  }

  try {
    // Decodificar token para extraer info (expiración y rol)
    const decoded: DecodedToken = jwtDecode(token);

    // Validar expiración del token
    if (decoded.exp * 1000 < Date.now()) {
      localStorage.removeItem("token"); // Eliminar token expirado
      return <Navigate to="/" replace />; // Redirigir al login
    }

    // Validar roles permitidos si se definieron
    if (roles?.length && (!decoded.roles || !decoded.roles.some(r => roles.includes(r)))) {
      return <Navigate to="/unauthorized" replace />;
    }

  } catch {
    // Si hay error decodificando el token, redirigir al login
    return <Navigate to="/" replace />;
  }

  // Si todo está correcto, renderiza los hijos
  return children;
};

// Exporta el componente para usarlo en rutas privadas
export default PrivateRoute;
